/**
 * Logger system for frontend applications
 * Provides consistent logging with levels, formatting, and environment-based behavior
 */

// Log levels in order of severity
export enum LogLevel {
  DEBUG = 0,
  INFO = 1,
  WARN = 2,
  ERROR = 3,
  NONE = 4, // Use to disable all logging
}

// Customizable options for the logger
export interface LoggerOptions {
  level: LogLevel;
  prefix?: string;
  enableTimestamp?: boolean;
  disableInProduction?: boolean;
  enableTracing?: boolean; // Include stack trace for errors
}

// Default options
const DEFAULT_OPTIONS: LoggerOptions = {
  level: (import.meta.env?.PROD as boolean) ? LogLevel.ERROR : LogLevel.DEBUG,
  prefix: '[Dendromap]',
  enableTimestamp: true,
  disableInProduction: true,
  enableTracing: import.meta.env?.DEV as boolean,
};

// Main Logger class
export class Logger {
  private options: LoggerOptions;
  private source?: string;

  constructor(source?: string, options?: Partial<LoggerOptions>) {
    this.source = source;
    this.options = { ...DEFAULT_OPTIONS, ...options };
  }

  /**
   * Checks if logging is enabled based on environment and options
   */
  private isLoggingEnabled(level: LogLevel): boolean {
    // If in production and logs are disabled, don't log
    if (
      (import.meta.env?.PROD as boolean) &&
      this.options.disableInProduction
    ) {
      return level >= LogLevel.ERROR; // Only log errors in production by default
    }

    // Check if the current log level is enabled
    return level >= this.options.level;
  }

  /**
   * Format log message with prefixes, timestamps, etc.
   */
  private formatMessage(message: string): string {
    const parts: string[] = [];

    // Add timestamp if enabled
    if (this.options.enableTimestamp) {
      parts.push(`[${new Date().toISOString()}]`);
    }

    // Add application prefix
    if (this.options.prefix) {
      parts.push(this.options.prefix);
    }

    // Add source component/module name if provided
    if (this.source) {
      parts.push(`[${this.source}]`);
    }

    // Combine all parts with the actual message
    return [...parts, message].join(' ');
  }

  /**
   * Get console styling based on log level
   */
  private getStyleForLevel(level: LogLevel): string[] {
    switch (level) {
      case LogLevel.DEBUG:
        return ['color: #6c757d', 'color: inherit']; // Grey
      case LogLevel.INFO:
        return ['color: #0d6efd', 'color: inherit']; // Blue
      case LogLevel.WARN:
        return ['color: #ffc107', 'color: inherit']; // Yellow
      case LogLevel.ERROR:
        return ['color: #dc3545', 'color: inherit']; // Red
      default:
        return ['color: inherit', 'color: inherit'];
    }
  }

  /**
   * Internal log method that handles all logging
   */
  private log(level: LogLevel, message: string, ...args: any[]): void {
    if (!this.isLoggingEnabled(level)) return;

    const formattedMessage = this.formatMessage(message);
    const styles = this.getStyleForLevel(level);

    // Log with appropriate level and styling
    switch (level) {
      case LogLevel.DEBUG:
        console.debug(`%c${this.options.prefix}%c ${formattedMessage}`, ...styles, ...args);
        break;
      case LogLevel.INFO:
        console.info(`%c${this.options.prefix}%c ${formattedMessage}`, ...styles, ...args);
        break;
      case LogLevel.WARN:
        console.warn(`%c${this.options.prefix}%c ${formattedMessage}`, ...styles, ...args);
        break;
      case LogLevel.ERROR:
        console.error(`%c${this.options.prefix}%c ${formattedMessage}`, ...styles, ...args);
        
        // Add stack trace for errors if enabled
        if (this.options.enableTracing && args[0] instanceof Error) {
          console.error(args[0]);
        }
        break;
    }
  }

  /**
   * Log debug message
   */
  debug(message: string, ...args: any[]): void {
    this.log(LogLevel.DEBUG, message, ...args);
  }

  /**
   * Log info message
   */
  info(message: string, ...args: any[]): void {
    this.log(LogLevel.INFO, message, ...args);
  }

  /**
   * Log warning message
   */
  warn(message: string, ...args: any[]): void {
    this.log(LogLevel.WARN, message, ...args);
  }

  /**
   * Log error message
   */
  error(message: string, ...args: any[]): void {
    this.log(LogLevel.ERROR, message, ...args);
  }

  /**
   * Log an object with formatting
   */
  logObject(level: LogLevel, label: string, obj: any): void {
    if (!this.isLoggingEnabled(level)) return;
    
    // Create a group in the console for better readability
    const formattedLabel = this.formatMessage(label);
    const styles = this.getStyleForLevel(level);
    
    console.groupCollapsed(`%c${this.options.prefix}%c ${formattedLabel}`, ...styles);
    console.dir(obj);
    console.groupEnd();
  }

  /**
   * Track performance with console.time/timeEnd
   */
  trackPerformance(label: string, callback: () => any): any {
    if (!this.isLoggingEnabled(LogLevel.DEBUG)) {
      return callback();
    }

    const timeLabel = `${this.options.prefix || ''}[${this.source || ''}] ${label}`;
    console.time(timeLabel);
    
    try {
      const result = callback();
      console.timeEnd(timeLabel);
      return result;
    } catch (error) {
      console.timeEnd(timeLabel);
      throw error;
    }
  }

  /**
   * Track performance of async functions
   */
  async trackAsyncPerformance<T>(label: string, callback: () => Promise<T>): Promise<T> {
    if (!this.isLoggingEnabled(LogLevel.DEBUG)) {
      return callback();
    }

    const timeLabel = `${this.options.prefix || ''}[${this.source || ''}] ${label}`;
    console.time(timeLabel);
    
    try {
      const result = await callback();
      console.timeEnd(timeLabel);
      return result;
    } catch (error) {
      console.timeEnd(timeLabel);
      throw error;
    }
  }

  /**
   * Create a child logger with a specific source
   */
  createChildLogger(source: string): Logger {
    return new Logger(source, this.options);
  }

  /**
   * Update logger options
   */
  updateOptions(options: Partial<LoggerOptions>): void {
    this.options = { ...this.options, ...options };
  }
}

// Default instance for quick import
const defaultLogger = new Logger();

export default defaultLogger;
